import { useState, useEffect } from 'react';
import { 
  EnvelopeSimple, 
  CaretDown,
  CaretRight,
  ArrowClockwise,
  PaperPlaneTilt,
  ArrowDown
} from '@phosphor-icons/react';
import { getEmailThreads, getEmailThread } from '../lib/api';
import { toast } from 'sonner';

interface EmailMessage {
  id: string;
  direction?: string;
  from?: string;
  from_email?: string;
  to?: string;
  subject?: string;
  body?: string;
  body_text?: string;
  created_at?: string;
}

interface EmailThread {
  id: string;
  reference?: string;
  subject?: string;
  participants?: string[];
  message_count?: number;
  last_message_at?: string;
}

export default function SuperAdminEmailsPage() {
  const [threads, setThreads] = useState<EmailThread[]>([]);
  const [expandedThreads, setExpandedThreads] = useState<Record<string, boolean>>({});
  const [threadMessages, setThreadMessages] = useState<Record<string, EmailMessage[]>>({});
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchThreads();
  }, []);

  const fetchThreads = async () => {
    try {
      const { data } = await getEmailThreads();
      setThreads(data);
    } catch (error) {
      console.error('Failed to fetch threads:', error);
      toast.error('Kunde inte hämta e-posttrådar');
    } finally {
      setLoading(false);
    }
  };

  const toggleThread = async (threadId: string) => {
    const isExpanded = expandedThreads[threadId];
    
    if (isExpanded) {
      // Collapse
      setExpandedThreads(prev => ({ ...prev, [threadId]: false }));
    } else {
      // Expand and fetch messages if not loaded
      setExpandedThreads(prev => ({ ...prev, [threadId]: true }));
      
      if (!threadMessages[threadId]) {
        setLoadingMessages(prev => ({ ...prev, [threadId]: true }));
        try {
          const { data } = await getEmailThread(threadId);
          setThreadMessages(prev => ({ ...prev, [threadId]: (data as { messages?: EmailMessage[] }).messages || [] }));
        } catch (error) {
          console.error('Failed to fetch messages:', error);
          toast.error('Kunde inte hämta meddelanden');
        } finally {
          setLoadingMessages(prev => ({ ...prev, [threadId]: false }));
        }
      }
    }
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString('sv-SE', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-4 sm:p-6 md:p-8" data-testid="superadmin-emails-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-slate-900">E-posttrådar</h1>
          <p className="text-slate-500 text-sm mt-1">Alla inkommande och utgående e-postmeddelanden</p>
        </div>
        <button
          onClick={fetchThreads}
          className="p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
          title="Uppdatera"
        >
          <ArrowClockwise size={20} />
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white border border-slate-200 rounded-lg p-4 animate-pulse">
              <div className="h-4 w-32 bg-slate-200 rounded mb-2" />
              <div className="h-3 w-48 bg-slate-200 rounded" />
            </div>
          ))}
        </div>
      ) : threads.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
          <EnvelopeSimple size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Inga e-posttrådar ännu</p>
        </div>
      ) : (
        <div className="space-y-3">
          {threads.map((thread) => {
            const isExpanded = expandedThreads[thread.id];
            const messages = threadMessages[thread.id] || [];
            const isLoadingMsg = loadingMessages[thread.id];
            
            return (
              <div
                key={thread.id}
                className="bg-white border border-slate-200 rounded-lg overflow-hidden"
              >
                {/* Thread Header - Clickable */}
                <button
                  onClick={() => toggleThread(thread.id)}
                  className="w-full p-4 text-left hover:bg-slate-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 text-slate-400">
                        {isExpanded ? <CaretDown size={16} /> : <CaretRight size={16} />}
                      </span>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {thread.reference && (
                            <span className="text-xs font-mono px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                              {thread.reference}
                            </span>
                          )}
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {thread.subject || '(Inget ämne)'}
                          </p>
                        </div>
                        <p className="text-xs text-slate-500">
                          {thread.participants?.slice(0, 2).join(', ')}
                        </p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-xs text-slate-400">
                            {thread.message_count} meddelanden
                          </span>
                          <span className="text-xs text-slate-300">•</span>
                          <span className="text-xs text-slate-400">
                            {formatDate(thread.last_message_at)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
                
                {/* Expanded Messages */}
                {isExpanded && (
                  <div className="border-t border-slate-100 bg-slate-50 p-4">
                    {isLoadingMsg ? (
                      <div className="text-center py-4">
                        <div className="animate-spin w-5 h-5 border-2 border-slate-300 border-t-slate-600 rounded-full mx-auto" />
                      </div>
                    ) : messages.length === 0 ? (
                      <p className="text-sm text-slate-400 text-center py-4">Inga meddelanden</p>
                    ) : (
                      <div className="space-y-3">
                        {messages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`p-3 rounded-lg ${
                              msg.direction === 'inbound' || msg.direction === 'incoming'
                                ? 'bg-white border border-slate-200' 
                                : 'bg-slate-800 text-white ml-8'
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              {msg.direction === 'inbound' || msg.direction === 'incoming' ? (
                                <ArrowDown size={12} className="text-blue-500" />
                              ) : (
                                <PaperPlaneTilt size={12} className={msg.direction === 'outbound' ? 'text-slate-400' : 'text-blue-400'} />
                              )}
                              <span className={`text-xs font-medium ${
                                msg.direction === 'outbound' ? 'text-slate-300' : 'text-slate-700'
                              }`}>
                                {msg.from_email}
                              </span>
                              <span className={`text-xs ml-auto ${
                                msg.direction === 'outbound' ? 'text-slate-400' : 'text-slate-400'
                              }`}>
                                {formatDate(msg.created_at)}
                              </span>
                            </div>
                            <p className={`text-sm whitespace-pre-wrap ${
                              msg.direction === 'outbound' ? 'text-slate-200' : 'text-slate-600'
                            }`}>
                              {msg.body || msg.body_text}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
